'use client'

import * as React from 'react'

import type { ClientVariant, FallbackCookies } from '../types'
import { makeVariantVisible, selectVariant } from '../scripts/head-scripts'
import { VariantSelectorScript } from '../scripts/body-scripts'
import { StaticVariant } from './static-variant'
import {
  isClientSideVariantSelection,
  isEdgeVariantSelection,
  isHtmlRenderVariantSelection,
  resetToClientSideVariantSelection,
} from '../utils/rendering-selection-state'
import { convertToLean } from '../utils/convert-to-lean'

function StaticVariantsRendererClientInternal({
  variants,
  fallbackCookies = {},
  renderingId,
}: {
  variants: ClientVariant[]
  fallbackCookies?: FallbackCookies
  renderingId: string
}) {
  const hasMountedRef = React.useRef(false)

  React.useEffect(() => {
    if (
      (isClientSideVariantSelection() || isHtmlRenderVariantSelection()) &&
      !hasMountedRef.current
    ) {
      //Check if the renderingId already has a style applied to it
      const existingStyle = document.querySelector(
        `.static-variant-visible-style[data-rendering-id="${renderingId}"]`,
      )

      const variantId = selectVariant({ variants: convertToLean(variants), fallbackCookies })

      // oxlint-disable-next-line unicorn/prefer-dom-node-dataset
      if (variantId && variantId !== existingStyle?.getAttribute('data-variant-id')) {
        existingStyle?.remove()
      }
      makeVariantVisible(variantId, renderingId)
    }
    hasMountedRef.current = true
  }, [renderingId, variants, fallbackCookies])

  const staticVariantsData = React.useMemo(
    () => ({
      variants: convertToLean(variants),
      renderingId,
      fallbackCookies,
    }),
    [variants, renderingId, fallbackCookies],
  )

  return (
    <span
      id={renderingId}
      // oxlint-disable-next-line no-negated-condition
      className={!isEdgeVariantSelection() ? 'static-variant-renderer' : undefined}
      suppressHydrationWarning={true}
      data-variants-data={
        // oxlint-disable-next-line no-negated-condition
        !isEdgeVariantSelection() ? JSON.stringify(staticVariantsData) : undefined
      }
    >
      {variants.map(variant => (
        <StaticVariant key={variant.id} variant={variant} renderingId={renderingId} />
      ))}

      <VariantSelectorScript renderingId={renderingId} />
      <SwitchBackToClientSideVariantSelection />
    </span>
  )
}

function SwitchBackToClientSideVariantSelection() {
  React.useEffect(() => {
    resetToClientSideVariantSelection()
  }, [])
  return null
}

export const StaticVariantsRendererClient = React.memo(StaticVariantsRendererClientInternal)
